import { useApplicationInsights } from "@packsize/application-monitoring";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import type { HydratedUser } from "@packsize/identity-api-sdk";

import { useIdentityClient, useToken } from "../../hooks";
import { type LicenseTier } from "../../schemas";
import type { LicensedProductCategoryName } from "../../types";
import {
	hasAnyRole as hasAnyRoleFromUser,
	hasLicenseByFlag,
	hasLicenseByTier,
	isPacksizeUser as isPacksizeUserUtil,
	isPacksizePartner as isPacksizePartnerUtil,
} from "../../utils";

import { UserContext } from "./user.context";

interface UserProviderProps {
	children: ReactNode;
}

const LOG_PREFIX = "[@packsize/authentication | UserProvider]";

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
	const applicationInsights = useApplicationInsights();

	const { tokenValid, token } = useToken();
	const [currentUser, setCurrentUser] = useState<HydratedUser | null | undefined>(undefined);

	const { client } = useIdentityClient();

	const refreshUser = useCallback(async () => {
		console.debug(LOG_PREFIX, "refreshUser called", { tokenValid });

		if (tokenValid === undefined) {
			console.debug(LOG_PREFIX, "refreshUser — token validity unknown, skipping");
			return;
		}

		if (tokenValid !== true || token === null) {
			console.debug(LOG_PREFIX, "refreshUser — token invalid or null, clearing user", {
				tokenValid,
				hasToken: token !== null,
			});
			setCurrentUser(null);
			return;
		}

		console.debug(LOG_PREFIX, "refreshUser — token valid, hydrating user");
		const response = await client.hydrateUser();

		if (response.success === false || Boolean(response.data) === false) {
			console.debug(LOG_PREFIX, "refreshUser — hydrateUser failed or returned no data", {
				success: response.success,
				hasData: Boolean(response.data),
			});
			setCurrentUser(null);
			return;
		}

		console.debug(LOG_PREFIX, "refreshUser — user hydrated successfully", {
			userId: response.data?.id,
			userName: response.data?.userName,
			tenantId: response.data?.tenantId,
			coreLicenseTier: response.data?.coreLicenseTier,
			dimensioningLicenseTier: response.data?.dimensioningLicenseTier,
			reportingLicenseTier: response.data?.reportingLicenseTier,
		});
		setCurrentUser(response.data);
	}, [client, tokenValid, token]);

	useEffect(() => {
		if (tokenValid === undefined) {
			return;
		}

		if (tokenValid === true) {
			refreshUser();
			return;
		}

		setCurrentUser(null);
	}, [refreshUser, tokenValid]);

	const isPacksizeUser = useMemo(() => {
		if (currentUser === null || currentUser === undefined) {
			return undefined;
		}

		return isPacksizeUserUtil(currentUser.permissions);
	}, [currentUser]);

	const isPacksizePartner = useMemo(() => {
		if (currentUser === null || currentUser === undefined) {
			return undefined;
		}

		return isPacksizePartnerUtil(currentUser.permissions);
	}, [currentUser]);

	const hasLicense = useCallback(
		(
			product: LicensedProductCategoryName,
			tier: LicenseTier,
			licenseFeatureFlag?: string | string[],
		): boolean | undefined => {
			if (currentUser === undefined) {
				console.debug(LOG_PREFIX, "hasLicense — user not yet loaded, returning undefined", {
					product,
					tier,
					licenseFeatureFlag,
				});
				return undefined;
			}

			const byTier = hasLicenseByTier(currentUser, product, tier);
			const byFlag = licenseFeatureFlag ? hasLicenseByFlag(currentUser, licenseFeatureFlag) : false;

			console.debug(LOG_PREFIX, "hasLicense — evaluation", {
				product,
				tier,
				licenseFeatureFlag,
				byTier,
				byFlag,
				userId: currentUser?.id,
				tenantId: currentUser?.tenantId,
			});

			if (byTier === false && byFlag === true) {
				console.debug(LOG_PREFIX, "hasLicense — LICENSE MISMATCH detected, granting access via feature flag", {
					product,
					requiredTier: tier,
					licenseFeatureFlag,
				});

				applicationInsights.trackEvent({
					name: "LicenseMismatch",
					properties: {
						product,
						requiredTier: tier,
						licenseFeatureFlag,
					},
				});

				return true;
			}

			return byTier;
		},
		[currentUser, applicationInsights],
	);

	const hasAnyRole = useCallback(
		(roles: string[]): boolean | undefined => {
			if (currentUser === undefined) {
				return undefined;
			}

			return hasAnyRoleFromUser(currentUser, roles);
		},
		[currentUser],
	);

	const value = useMemo(
		() => ({
			currentUser,
			isPacksizeUser,
			isPacksizePartner,
			hasAnyRole,
			hasLicense,
			refreshUser,
			setUser: setCurrentUser,
		}),
		[currentUser, hasAnyRole, hasLicense, refreshUser, isPacksizeUser, isPacksizePartner],
	);

	return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
